<template>
    <div class="daily-agent-activity-chart-main px-2">
        <div class="header">
            <span>{{ $t("report.agentActivity") }}</span>
            <span class="sDate">
                <a-button type="dashed" shape="circle" icon="left" @click="goPreviousDay" />
                {{ getStartDateHumanize() }}
                <a-button type="dashed" shape="circle" icon="right" @click="goNextDay" :disabled="nextDayAvailable()" />
            </span>
            <span></span>
        </div>
        <div class="toolbar px-1">
            <div class="toolbar-settings">
                <div class="toolbar-item">
                    <label class="text-uppercase pt-2 w-100">{{ $t("message.hideInactiveAgents") }}</label>
                    <md-switch v-model="hideInactiveAgents" class="md-primary ml-3" />
                </div>
                <div class="toolbar-item">
                    <label class="text-uppercase pt-2 w-100">{{ $t("message.hideMinorValues") }}</label>
                    <md-switch v-model="hideMinorValues" class="md-primary ml-3" />
                </div>
            </div>
            <div class="toolbar-tick">
                <span>12 AM</span>
                <span>4 AM</span>
                <span>8 AM</span>
                <span>12 PM</span>
                <span>4 PM</span>
                <span>8 PM</span>
                <span>12 AM</span>
            </div>
        </div>
        <div class="chart-rows" :key="Math.random() * 100">
            <a-skeleton avatar :paragraph="{ rows: 1 }" v-if="loading" />
            <a-skeleton avatar :paragraph="{ rows: 1 }" v-if="loading" />
            <a-skeleton avatar :paragraph="{ rows: 1 }" v-if="loading" />
            <a-skeleton avatar :paragraph="{ rows: 1 }" v-if="loading" />
            <a-skeleton avatar :paragraph="{ rows: 1 }" v-if="loading" />

            <div class="chart-row" v-for="item in getFilteredItems()" v-if="!loading" :key="Math.random()">
                <div class="agent-info">
                    <a-avatar
                        :size="50"
                        :src="item.agent.gravatar ? item.agent.gravatar : null"
                        :username="item.agent.gravatar ? null : item.agent.name"
                        style="
                            background-color: rgb(255 255 255) !important;
                            color: #368dda;
                            font-size: 25px;
                            margin: initial;
                        "
                        >{{ item.agent.gravatar ? null : item.agent.name.substr(0, 1).toUpperCase() }}
                    </a-avatar>
                    <div class="d-flex flex-column justify-content-center align-items-center pl-2">
                        <span>{{ item.agent.name }}</span>
                        <span>{{ item.agent.email }}</span>
                    </div>
                </div>
                <div class="agent-bar">
                    <AgentActivityBar
                        :activities="item.actions"
                        :last-activity="item.lastAction"
                        :start-day="startDate"
                        :hide-inactive="hideInactiveAgents"
                        :hide-minor-values="hideMinorValues"
                    />
                </div>
            </div>
            <div v-if="!loading && !items.length">
                <a-list :data-source="[]" />
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { getAgentActivities } from "../../api/statistics";
import AgentActivityBar from "./AgentActivityBar";
import { log2 } from "../../utils/log2";
import * as _ from "lodash";

const { getPercentsFromUserActions, calculateStatus } = require("./utils");

const log = log2("DailyAgentActivities");
export default {
    name: "DailyAgentActivities",
    components: {
        AgentActivityBar,
    },
    props: {
        initialStartDate: {
            type: Number,
            default: moment().startOf("day").valueOf(),
        },
        selectedUsers: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            startDate: moment(this.initialStartDate).startOf("day").valueOf(),
            endDate: moment(this.initialStartDate).endOf("day").valueOf(),
            items: [],
            hideInactiveAgents: true,
            hideMinorValues: true,
            loading: true,
        };
    },
    mounted() {
        log("mounted");
        this.startDate = this.initialStartDate;
        this.endDate = moment(this.initialStartDate).endOf("day").valueOf();
        this.fetchData();
    },
    updated() {
        log("render", this.items);
    },
    methods: {
        goNextDay() {
            this.startDate = moment(this.startDate).add(1, "day").valueOf();
            this.endDate = moment(this.endDate).add(1, "day").valueOf();
            this.fetchData();
        },
        goPreviousDay() {
            this.startDate = moment(this.startDate).subtract(1, "day").valueOf();
            this.endDate = moment(this.endDate).subtract(1, "day").valueOf();
            this.fetchData();
        },
        nextDayAvailable() {
            return moment(this.startDate).add(1, "day").valueOf() > moment().endOf("day").valueOf();
        },
        async fetchData() {
            log("fetchData", this.startDate, this.endDate);
            const req = {
                startDate: moment(this.startDate).startOf("day").toISOString(),
                endDate: moment(this.endDate).endOf("day").toISOString(),
                users: this.selectedUsers,
            };
            this.loading = true;
            let { data } = await getAgentActivities(req).catch((err) => {
                this.loading = false;
                return err;
            });
            log("fetched", data);
            const { items, startDate, endDate } = data;
            log("order result 1", {
                before: items.map((x) => x.agent.name),
            });
            this.items = _.sortBy(items, (q) => q.agent.name.toLowerCase());
            log("order result 2", {
                before: items.map((x) => x.agent.name),
                after: this.items.map((x) => x.agent.name),
            });
            this.loading = false;
        },
        getStartDateHumanize() {
            return moment(this.startDate).format("LL");
        },
        getFilteredItems() {
            log("getFilteredItems", this.hideInactiveAgents, this.items);
            return this.hideInactiveAgents
                ? this.items.filter((item) => {
                      const isNonEmpty = !!item.lastAction || item.actions.length > 0;
                      if (isNonEmpty) {
                          const percents = getPercentsFromUserActions(
                              item.actions,
                              item.lastAction,
                              this.startDate,
                              item.agent.name
                          );
                          const { acceptingChatDuration, notAcceptingChatDuration } = calculateStatus(percents);
                          return acceptingChatDuration + notAcceptingChatDuration > 0;
                      } else {
                          return false;
                      }
                  })
                : this.items;
        },
    },
    watch: {
        initialStartDate(newVal, oldVal) {
            log("initialStartDate changed", newVal);
            if (oldVal !== newVal) {
                this.startDate = newVal;
                this.endDate = moment(newVal).endOf("day").valueOf();
                this.fetchData();
            }
        },
        selectedUsers(newVal, oldVal) {
            this.fetchData();
        },
    },
};
</script>

<style lang="scss">
.daily-agent-activity-chart-main {
    display: flex;
    flex-direction: column;
    border: 1px solid #e9edee;
    min-height: 300px;
    width: 100%;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        border-bottom: 1px solid #e9edee;
        min-height: 50px;
        padding: 5px;

        .sDate {
            font-weight: bold;
            color: #262626;
            font-size: 17px;
        }
    }

    .toolbar {
        border-bottom: 1px solid #e9edee;
        min-height: 25px;
        display: flex;
        width: 100%;

        .toolbar-settings {
            display: flex;
            justify-content: flex-start;
            //align-items: center;
            //border: 1px solid red;
            color: #bcc0c4;
            width: 30%;
            flex-direction: column;

            div.toolbar-item {
                height: 20px;
                display: flex;
                justify-content: space-evenly;
                align-items: center;
                padding-right: 20px;
                margin-top: 4px;

                .md-switch {
                    margin: 0 !important;
                }
            }
        }

        .toolbar-tick {
            display: flex;
            justify-content: space-between;
            width: 70%;
            align-items: center;
            font-weight: 500;
        }
    }

    .chart-rows {
        display: flex;
        flex-direction: column;
        width: 100%;

        .chart-row {
            min-height: 75px;
            width: 100%;
            display: flex;
            border-bottom: 1px solid #e9edee;
            cursor: pointer;

            &:hover {
                background: #d9d9d933;
            }

            .agent-info {
                width: 30%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                padding-left: 5px;
            }

            .agent-bar {
                width: 70%;
            }
        }
    }
}
</style>
